/**
 * ═══════════════════════════════════════════════════════════════
 * WASP Frontend v2.0 - Performance App Types
 * ═══════════════════════════════════════════════════════════════
 * 
 * @file types/performance_types.ts
 * @version 1.0.0
 * @description Interfaces y tipos para el sistema de Performance Metrics
 * @created 2025-10-30 - Fase 2 Session 1
 * 
 * FEATURES:
 * - Tipos seguros para métricas de WhatsApp
 * - Interfaces para gráficos y visualizaciones
 * - Tipos para filtros y exportación
 * - Compatibilidad con endpoints backend
 * - JSDoc completo para documentación
 */

// ═══════════════════════════════════════════════════════════════
// ENUMS Y CONSTANTES
// ═══════════════════════════════════════════════════════════════

/**
 * Rangos de tiempo disponibles para análisis
 */
export enum TimeRange {
  LAST_24H = '24h',
  LAST_7D = '7d',
  LAST_30D = '30d',
  CUSTOM = 'custom'
}

/**
 * Estados de mensaje de WhatsApp
 */
export enum MessageStatus {
  SENT = 'sent',
  DELIVERED = 'delivered',
  READ = 'read',
  FAILED = 'failed',
  PENDING = 'pending'
}

/**
 * Tipos de mensaje
 */
export enum MessageType {
  TEXT = 'text',
  IMAGE = 'image',
  DOCUMENT = 'document',
  AUDIO = 'audio',
  VIDEO = 'video',
  LOCATION = 'location',
  CONTACT = 'contact'
}

/**
 * Formatos de exportación
 */
export enum ExportFormat {
  CSV = 'csv',
  PDF = 'pdf',
  JSON = 'json'
}

/**
 * Dirección del mensaje
 */
export enum MessageDirection {
  OUTGOING = 'outgoing',
  INCOMING = 'incoming'
}

// ═══════════════════════════════════════════════════════════════
// INTERFACES PRINCIPALES
// ═══════════════════════════════════════════════════════════════

/**
 * Métricas principales de performance
 */
export interface PerformanceMetrics {
  /** Total de mensajes enviados */
  totalSent: number;
  
  /** Total de mensajes recibidos */
  totalReceived: number;
  
  /** Mensajes entregados exitosamente */
  delivered: number;
  
  /** Mensajes leídos */
  read: number;
  
  /** Mensajes fallidos */
  failed: number;
  
  /** Mensajes pendientes */
  pending: number;
  
  /** Tasa de entrega (%) */
  deliveryRate: number;
  
  /** Tasa de lectura (%) */
  readRate: number;
  
  /** Tasa de respuesta (%) */
  responseRate: number;
  
  /** Tiempo promedio de respuesta (minutos) */
  avgResponseTime: number;
  
  /** Conversaciones activas */
  activeConversations: number;
  
  /** Conversaciones totales */
  totalConversations: number;
  
  /** Periodo de análisis */
  timeRange: TimeRange;
  
  /** Fecha de última actualización */
  lastUpdated: Date;
}

/**
 * Estadísticas detalladas de mensajes
 */
export interface MessageStats {
  /** ID único de la estadística */
  id: string;
  
  /** Timestamp */
  timestamp: Date;
  
  /** Cantidad de mensajes por estado */
  byStatus: {
    [key in MessageStatus]: number;
  };
  
  /** Cantidad de mensajes por tipo */
  byType: {
    [key in MessageType]: number;
  };
  
  /** Cantidad de mensajes por dirección */
  byDirection: {
    [key in MessageDirection]: number;
  };
  
  /** Mensajes por hora del día */
  byHour: number[];
  
  /** Mensajes por día de la semana */
  byWeekday: number[];
}

/**
 * Item de conversación individual
 */
export interface ConversationItem {
  /** ID único de la conversación */
  id: string;
  
  /** Nombre del contacto */
  contactName: string;
  
  /** Número de teléfono */
  phoneNumber: string;
  
  /** Último mensaje */
  lastMessage: string;
  
  /** Timestamp del último mensaje */
  lastMessageTime: Date;
  
  /** Cantidad de mensajes no leídos */
  unreadCount: number;
  
  /** Estado de la conversación */
  status: 'active' | 'pending' | 'closed';
  
  /** Total de mensajes en la conversación */
  messageCount: number;
  
  /** Dirección del último mensaje */
  lastMessageDirection: MessageDirection;
  
  /** Avatar URL (opcional) */
  avatarUrl?: string;
  
  /** Tags de la conversación */
  tags?: string[];
  
  /** Asignado a (usuario) */
  assignedTo?: string;
}

/**
 * Datos para gráficos de tendencias
 */
export interface TrendData {
  /** Etiqueta del punto (fecha/hora) */
  label: string;
  
  /** Timestamp */
  timestamp: Date;
  
  /** Valor principal */
  value: number;
  
  /** Valor de comparación (opcional) */
  compareValue?: number;
  
  /** Valores adicionales para múltiples series */
  values?: {
    sent?: number;
    received?: number;
    delivered?: number;
    read?: number;
  };
}

/**
 * Configuración de rango de tiempo personalizado
 */
export interface CustomTimeRange {
  /** Fecha de inicio */
  startDate: Date;
  
  /** Fecha de fin */
  endDate: Date;
}

/**
 * Opciones de filtrado
 */
export interface FilterOptions {
  /** Rango de tiempo */
  timeRange: TimeRange;
  
  /** Rango personalizado (si timeRange es CUSTOM) */
  customRange?: CustomTimeRange;
  
  /** Filtrar por estado de mensaje */
  status?: MessageStatus[];
  
  /** Filtrar por tipo de mensaje */
  messageType?: MessageType[];
  
  /** Filtrar por contacto (búsqueda) */
  searchQuery?: string;
  
  /** Filtrar por estado de conversación */
  conversationStatus?: ('active' | 'pending' | 'closed')[];
  
  /** Filtrar por tags */
  tags?: string[];
  
  /** Ordenar por */
  sortBy?: 'date' | 'name' | 'messageCount' | 'unreadCount';
  
  /** Orden ascendente/descendente */
  sortOrder?: 'asc' | 'desc';
  
  /** Página actual (para paginación) */
  page?: number;
  
  /** Items por página */
  pageSize?: number;
}

/**
 * Configuración de exportación
 */
export interface ExportConfig {
  /** Formato de exportación */
  format: ExportFormat;
  
  /** Incluir métricas generales */
  includeMetrics: boolean;
  
  /** Incluir conversaciones */
  includeConversations: boolean;
  
  /** Incluir gráficos (solo PDF) */
  includeCharts?: boolean;
  
  /** Rango de fechas */
  dateRange: TimeRange | CustomTimeRange;
  
  /** Nombre del archivo */
  filename?: string;
}

/**
 * Comparativa de periodo
 */
export interface PeriodComparison {
  /** Métrica actual */
  current: number;
  
  /** Métrica del periodo anterior */
  previous: number;
  
  /** Cambio absoluto */
  change: number;
  
  /** Cambio porcentual */
  changePercent: number;
  
  /** Tendencia (positiva/negativa) */
  trend: 'up' | 'down' | 'neutral';
}

/**
 * Respuesta de API para estadísticas
 */
export interface StatsAPIResponse {
  success: boolean;
  data: {
    metrics: PerformanceMetrics;
    trends: TrendData[];
    comparison?: {
      sent: PeriodComparison;
      received: PeriodComparison;
      responseRate: PeriodComparison;
      avgResponseTime: PeriodComparison;
    };
  };
  timestamp: string;
}

/**
 * Respuesta de API para conversaciones
 */
export interface ConversationsAPIResponse {
  success: boolean;
  data: {
    conversations: ConversationItem[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  timestamp: string;
}

/**
 * Props para PerformanceApp (Main Component)
 */
export interface PerformanceAppProps {
  /** Mostrar en modo compacto */
  compact?: boolean;
  
  /** Callback al cambiar filtros */
  onFilterChange?: (filters: FilterOptions) => void;
  
  /** Callback al exportar datos */
  onExport?: (config: ExportConfig) => void;
  
  /** Altura personalizada */
  height?: number | string;
}

/**
 * Props para MetricsOverview
 */
export interface MetricsOverviewProps {
  /** Métricas actuales */
  metrics: PerformanceMetrics;
  
  /** Comparativas con periodo anterior */
  comparison?: {
    sent: PeriodComparison;
    received: PeriodComparison;
    responseRate: PeriodComparison;
    avgResponseTime: PeriodComparison;
  };
  
  /** Estado de carga */
  loading?: boolean;
  
  /** Modo compacto */
  compact?: boolean;
}

/**
 * Props para MessageTrendsChart
 */
export interface MessageTrendsChartProps {
  /** Datos de tendencias */
  data: TrendData[];
  
  /** Rango de tiempo seleccionado */
  timeRange: TimeRange;
  
  /** Callback al cambiar rango */
  onTimeRangeChange: (range: TimeRange) => void;
  
  /** Altura del gráfico */
  height?: number;
  
  /** Mostrar leyenda */
  showLegend?: boolean;
  
  /** Estado de carga */
  loading?: boolean;
}

/**
 * Props para ResponseRateGauge
 */
export interface ResponseRateGaugeProps {
  /** Tasa de respuesta actual (0-100) */
  value: number;
  
  /** Valor objetivo */
  target?: number;
  
  /** Tamaño del gauge */
  size?: number;
  
  /** Mostrar animación */
  animated?: boolean;
  
  /** Comparación con periodo anterior */
  comparison?: PeriodComparison;
}

/**
 * Props para ConversationsList
 */
export interface ConversationsListProps {
  /** Lista de conversaciones */
  conversations: ConversationItem[];
  
  /** Total de conversaciones */
  total: number;
  
  /** Página actual */
  page: number;
  
  /** Items por página */
  pageSize: number;
  
  /** Callback al cambiar página */
  onPageChange: (page: number) => void;
  
  /** Callback al seleccionar conversación */
  onSelectConversation?: (conversation: ConversationItem) => void;
  
  /** Opciones de filtrado */
  filters: FilterOptions;
  
  /** Callback al cambiar filtros */
  onFilterChange: (filters: FilterOptions) => void;
  
  /** Estado de carga */
  loading?: boolean;
}

/**
 * Props para ExportReportButton
 */
export interface ExportReportButtonProps {
  /** Métricas a exportar */
  metrics: PerformanceMetrics;
  
  /** Conversaciones a exportar */
  conversations?: ConversationItem[];
  
  /** Callback al exportar */
  onExport: (config: ExportConfig) => void;
  
  /** Estado de exportación */
  exporting?: boolean;
  
  /** Mostrar opciones avanzadas */
  showAdvancedOptions?: boolean;
}

// ═══════════════════════════════════════════════════════════════
// HELPERS Y UTILIDADES
// ═══════════════════════════════════════════════════════════════

/**
 * Estado del componente Performance App
 */
export interface PerformanceAppState {
  metrics: PerformanceMetrics | null;
  trends: TrendData[];
  conversations: ConversationItem[];
  filters: FilterOptions;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

/**
 * Configuración de colores para gráficos
 */
export interface ChartColors {
  sent: string;
  received: string;
  delivered: string;
  read: string;
  failed: string;
  pending: string;
  background: string;
  text: string;
  grid: string;
  tooltip: string;
}

/**
 * Punto de datos para tooltip
 */
export interface TooltipData {
  label: string;
  value: number;
  color: string;
  timestamp?: Date;
}

/**
 * Configuración de animación
 */
export interface AnimationConfig {
  duration: number;
  easing: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';
  delay?: number;
}

// ═══════════════════════════════════════════════════════════════
// VALORES POR DEFECTO
// ═══════════════════════════════════════════════════════════════

/**
 * Filtros por defecto
 */
export const DEFAULT_FILTERS: FilterOptions = {
  timeRange: TimeRange.LAST_24H,
  sortBy: 'date',
  sortOrder: 'desc',
  page: 1,
  pageSize: 20
};

/**
 * Colores por defecto (tema azul)
 */
export const DEFAULT_CHART_COLORS: ChartColors = {
  sent: '#0d6efd',      // Bootstrap primary blue
  received: '#0dcaf0',  // Bootstrap info cyan
  delivered: '#198754', // Bootstrap success green
  read: '#20c997',      // Bootstrap teal
  failed: '#dc3545',    // Bootstrap danger red
  pending: '#ffc107',   // Bootstrap warning yellow
  background: 'var(--bs-body-bg)',
  text: 'var(--bs-body-color)',
  grid: 'var(--bs-border-color)',
  tooltip: 'var(--bs-dark)'
};

/**
 * Configuración de animación por defecto
 */
export const DEFAULT_ANIMATION: AnimationConfig = {
  duration: 300,
  easing: 'ease-in-out',
  delay: 0
};

// ═══════════════════════════════════════════════════════════════
// TYPE GUARDS
// ═══════════════════════════════════════════════════════════════

/**
 * Verifica si un TimeRange es personalizado
 */
export function isCustomTimeRange(range: TimeRange | CustomTimeRange): range is CustomTimeRange {
  return typeof range === 'object' && 'startDate' in range && 'endDate' in range;
}

/**
 * Verifica si las métricas son válidas
 */
export function isValidMetrics(metrics: any): metrics is PerformanceMetrics {
  return (
    metrics &&
    typeof metrics.totalSent === 'number' &&
    typeof metrics.totalReceived === 'number' &&
    typeof metrics.deliveryRate === 'number' &&
    typeof metrics.readRate === 'number' &&
    typeof metrics.responseRate === 'number'
  );
}

/**
 * Verifica si una conversación es válida
 */
export function isValidConversation(conv: any): conv is ConversationItem {
  return (
    conv &&
    typeof conv.id === 'string' &&
    typeof conv.contactName === 'string' &&
    typeof conv.phoneNumber === 'string' &&
    typeof conv.lastMessage === 'string'
  );
}